/**
 * Phase 129 (Plan 129-08, D-13, D-20) and Phase 130 (Plan 130-11, 130-15, D-01) — the Repository
 * policies card of the project Engineering tab.
 *
 * What the merge gate and the deploy check resolved for THIS project's Robot repositories, read-only
 * (`RepoPolicyReadOnly`): the policy of a repository is its `.monkfactory/factory.yml` on main,
 * changed by a reviewed pull request, so the card has no editor, no mutation hook and no reset. A
 * repository is shared by every project whose Robot lists it; per row the card says with how many
 * other projects. Rendered only on an instance with the Robot / Factory add-on.
 *
 * Phase 131 (Plan 131-05, D-11): behind `mks_547_factory_onboarding_generator`, a repository whose
 * factory.yml is absent or invalid gets a read-only "Generate draft" button (`OnboardingDraftPanel`).
 *
 * The `mks_522_monkfactory_runtimes` flag stays registered until the automated release-flag cleanup
 * lands; both of its branches render the same read-only view, so that cleanup is a pure deletion of
 * the ternaries.
 */
import type { ReactElement } from 'react';
import { RepoPolicyReadOnly } from '../bot-admin/RepoPolicyReadOnly';
import { useProjectRobotView } from '../../hooks/useProjectEngineering';
import type { ProjectEngineeringPolicyItem } from '../../services/api/projectEngineering';
import { useRobotEnabled } from '../../hooks/useRobotEnabled';
import { useFlag } from '../../services/flagsContext';
import { OnboardingDraftPanel } from './OnboardingDraftPanel';

export interface ProjectPoliciesCardProps {
  projectId: number;
}

function sharedNote(otherProjects: number): string {
  return `Shared with ${otherProjects} other ${otherProjects === 1 ? 'project' : 'projects'}`;
}

function sharedNoteElement(item: ProjectEngineeringPolicyItem): ReactElement | null {
  if (item.otherProjects <= 0) return null;
  return (
    <p
      className="mt-1 text-xs text-amber-700"
      data-testid={`policy-shared-note-${item.repository}`}
    >
      {sharedNote(item.otherProjects)}
    </p>
  );
}

function PoliciesCardBody({ projectId }: ProjectPoliciesCardProps): ReactElement {
  const viewQ = useProjectRobotView(projectId);
  const view = viewQ.data;
  const repositories = view?.config?.repositories.map((r) => r.repository) ?? [];
  // Both flag branches render the read-only view (Plan 130-15); the flag is removed by its own cleanup.
  const runtimesOn = useFlag('mks_522_monkfactory_runtimes');
  // Phase 131 (D-11): the read-only Generate draft button, on rows whose factory.yml is not usable.
  const onboardingOn = useFlag('mks_547_factory_onboarding_generator');
  const allowed = new Set(repositories.map((r) => r.toLowerCase()));
  const visiblePolicies = (view?.policies ?? []).filter((p) =>
    allowed.has(p.repository.toLowerCase())
  );

  return (
    <section
      className="bg-white rounded-lg border border-gray-200 p-6"
      data-section="engineering-policies"
    >
      <h2 className="text-lg font-semibold text-gray-900">Repository policies</h2>
      <p className="mt-0.5 text-sm text-gray-500">
        What the merge gate and the deploy check resolved for each of this project&apos;s Robot
        repositories, and the state of its runner. A repository without a valid factory.yml uses
        the safe default: no required CI contexts, so the gate holds its pull requests for a
        human.
      </p>

      {viewQ.isLoading ? (
        <p className="mt-3 text-sm text-gray-400">Loading repository policies…</p>
      ) : viewQ.isError || !view ? (
        <p className="mt-3 text-sm text-red-600" role="alert">
          Failed to load the repository policies.
        </p>
      ) : repositories.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500" data-testid="policies-empty">
          This project&apos;s Robot lists no repository yet. Add one in the Robot card to see its
          policy here.
        </p>
      ) : (
        <>
          <p className="mt-3 text-sm text-gray-600" data-testid="policies-as-code-note">
            Rules live in .monkfactory/factory.yml on main; change them with a reviewed pull
            request.
          </p>
          <ul className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
            {visiblePolicies.map((item) => (
              <RepoPolicyReadOnly
                key={item.repository}
                item={item}
                note={
                  <>
                    {runtimesOn
                      ? sharedNoteElement(item)
                      : sharedNoteElement(item)}
                    {onboardingOn &&
                      (item.file.status === 'absent' || item.file.status === 'invalid') && (
                        <OnboardingDraftPanel projectId={projectId} repository={item.repository} />
                      )}
                  </>
                }
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export function ProjectPoliciesCard({ projectId }: ProjectPoliciesCardProps): ReactElement | null {
  const robotEnabled = useRobotEnabled();
  if (!robotEnabled) return null;
  return <PoliciesCardBody projectId={projectId} />;
}

export default ProjectPoliciesCard;
